import { planGraceDays } from "./plan-billing-frequency.ts";

export const PREPAID_EXPIRY_TIME_ZONE = "America/Bogota";
export const PREPAID_EXPIRY_UTC_OFFSET = "-05:00";
export const PREPAID_EXPIRY_NOTICE_DAYS = [7, 3] as const;
export const PREPAID_EXPIRING_WINDOW_DAYS = 7;

export type PrepaidExpiryPhase = "none" | "active" | "expiring" | "grace" | "expired";

export interface PrepaidExpiry {
  expiresOn: string | null;
  graceDays: number;
  graceUntil: string | null;
  blocksOn: string | null;
  daysLeft: number | null;
  phase: PrepaidExpiryPhase;
}

type GraceTerms = Parameters<typeof planGraceDays>[0];

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

function isRealDate(date: string): boolean {
  if (!DATE_ONLY.test(date)) return false;
  const at = new Date(`${date}T12:00:00Z`);
  return !Number.isNaN(at.getTime()) && at.toISOString().slice(0, 10) === date;
}

export function bogotaDateOf(value: string | Date | null | undefined): string | null {
  if (!value) return null;
  if (typeof value === "string" && DATE_ONLY.test(value)) return isRealDate(value) ? value : null;
  const at = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(at.getTime())) return null;
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: PREPAID_EXPIRY_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(at);
}

export function bogotaToday(now: Date = new Date()): string {
  return bogotaDateOf(now) as string;
}

export function shiftDate(date: string, days: number): string {
  const at = new Date(`${date}T12:00:00Z`);
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10);
}

export function calendarDaysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86_400_000);
}

export function prepaidExpiryInstant(date: string): string {
  return `${date}T00:00:00${PREPAID_EXPIRY_UTC_OFFSET}`;
}

export function prepaidExpiry(
  expiresAt: string | null | undefined,
  plan: GraceTerms,
  today: string,
): PrepaidExpiry {
  const graceDays = planGraceDays(plan);
  const expiresOn = bogotaDateOf(expiresAt ?? null);
  if (!expiresOn) {
    return { expiresOn: null, graceDays, graceUntil: null, blocksOn: null, daysLeft: null, phase: "none" };
  }
  const daysLeft = calendarDaysBetween(today, expiresOn);
  let phase: PrepaidExpiryPhase;
  if (daysLeft > PREPAID_EXPIRING_WINDOW_DAYS) phase = "active";
  else if (daysLeft >= 0) phase = "expiring";
  else if (-daysLeft <= graceDays) phase = "grace";
  else phase = "expired";
  return {
    expiresOn,
    graceDays,
    graceUntil: shiftDate(expiresOn, graceDays),
    blocksOn: shiftDate(expiresOn, graceDays + 1),
    daysLeft,
    phase,
  };
}

export function prepaidExpiryNoticeDays(daysLeft: number | null): number | null {
  if (daysLeft === null || daysLeft < 1) return null;
  const sorted = [...PREPAID_EXPIRY_NOTICE_DAYS].sort((a, b) => a - b);
  return sorted.find((notice) => daysLeft <= notice) ?? null;
}

export type RequestedPrepaidExpiry =
  | { ok: true; value: string }
  | { ok: false; error: string };

export function readPrepaidExpiryDate(raw: unknown, today: string): RequestedPrepaidExpiry {
  if (raw === undefined || raw === null || raw === "") {
    return { ok: false, error: "Los planes ya pagados requieren una fecha de vencimiento." };
  }
  const value = typeof raw === "string" ? bogotaDateOf(raw) : null;
  if (!value) return { ok: false, error: "La fecha de vencimiento no es válida." };
  if (value < today) return { ok: false, error: "La fecha de vencimiento no puede ser anterior a hoy." };
  return { ok: true, value };
}
